import React from "react";
import { useTranslation } from "react-i18next";
import Loader from "../../../loader/Loader";
import "./EditOrCreateCategoryModal.less";

export type EditOrCreateCategoryModalProps = {
  mode: "create" | "edit";
  initialName?: string;
  onConfirm: (name: string) => void | Promise<void>;
  onCancel: () => void;
};

const EditOrCreateCategoryModal: React.FC<EditOrCreateCategoryModalProps> = ({ mode, initialName = "", onConfirm, onCancel }) => {
  const { t } = useTranslation();
  const [name, setName] = React.useState<string>(initialName);
  const inputRef = React.useRef<HTMLInputElement | null>(null);
  const [submitting, setSubmitting] = React.useState(false);

  React.useEffect(() => {
    const timer = setTimeout(() => {
      if (mode === "edit") {
        inputRef.current?.select();
      }
    }, 0);
    return () => clearTimeout(timer);
  }, [mode]);

  const handleOverlayClick = () => {
    if (submitting) return;
    onCancel?.();
  };
  const handleModalClick = (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => e.stopPropagation();

  const isCreate = mode === "create";
  const title = isCreate ? t("edit-or-create-category-title-create") : t("edit-or-create-category-title-edit");

  const isValid = name.trim().length > 0 && (isCreate || name.trim() !== initialName.trim());
  const primaryDisabled = !isValid || submitting;

  const handlePrimaryClick = async () => {
    if (primaryDisabled) return;
    try {
      setSubmitting(true);
      await onConfirm(name.trim());
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="edit-or-create-category-overlay" onClick={handleOverlayClick}>
      <div className="edit-or-create-category-content" onClick={handleModalClick}>
        <div className="modal-header-line-wrapper">
          <div className="modal-header-line"></div>
        </div>
        <div className="edit-or-create-category-container">
          <div className="edit-or-create-category-title">{title}</div>

          <div className="edit-or-create-input-wrapper">
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={t("edit-or-create-category-input-placeholder") as string}
              className="edit-or-create-input"
              aria-label={t("edit-or-create-category-input-placeholder") as string}
              ref={inputRef}
              autoFocus
              disabled={submitting}
            />
            <label className={`edit-or-create-input-label ${name ? "has-value" : ""}`}>{t("bookmark")}</label>
          </div>

          <button
            type="button"
            className={`edit-or-create-primary ${primaryDisabled ? "disabled" : ""}`}
            onClick={handlePrimaryClick}
            disabled={primaryDisabled}
          >
            {submitting ? <Loader /> : isCreate ? t("create") : t("confirm")}
          </button>

          <button type="button" className="edit-or-create-cancel" onClick={onCancel} disabled={submitting}>
            {t("cancel")}
          </button>
        </div>
      </div>
    </div>
  );
};

export default EditOrCreateCategoryModal;
